// Global Instructions Rule Applied!
//
// Standalone journal-entry form (spec A3.5): header (fund, entry date,
// description, optional reference links to a fund transaction and/or an
// asset transaction) + the journal-lines editor (accounting item, Dr/Cr,
// amount, description, optional Investor for the LP subledger dimension) +
// running totals with a Balanced tag. Lifecycle: Save (Draft) -> Post ->
// Unbook / Delete, mirroring the fund-transaction form. Links live on the
// HEADER only - the engines own the journal-line dimension columns.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Col, Input, InputNumber, Modal, Row, Select, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../../core/components/DatePicker.tsx'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { TABLE_JOURNAL_ENTRIES } from '../../../../state_manager/globals.ts'
import { useNumberFormat, useNumberDisplay } from '../../../../core/utils/inputNumberFormat.ts'
import {
  fetchJournalEntryDetail,
  fetchJournalEntryOptions,
  saveJournalEntry,
  bookJournalEntry,
  unbookJournalEntry,
  trashJournalEntry
} from '../../services/journalEntriesService.ts'
import type {
  JournalEntryDetail,
  JournalEntryOptions,
  JournalEntrySavePayload
} from '../../../../state_manager/data_models/journal_entry_detail.ts'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const STATUS_LABEL_BOOKED = 'Booked'

interface LineRow {
  key: string
  id?: string
  acc_item_id: string | null
  debit_credit_id: 'dr' | 'cr'
  amount: number | null
  description: string
  fund_commitment_id: string | null
  // the LP as je__entry_detail names it: the holder on the line's date
  investor_name?: string | null
}

// A Booked line names its LP as the fund ledger does - the holder on the
// line's date - not by the picker's options, which name each commitment by
// its CURRENT holder (a later Transfer of Interest would rename the line).
const bookedLpOptions = (row: LineRow) =>
  row.fund_commitment_id ? [{ value: row.fund_commitment_id, label: row.investor_name ?? '' }] : []

let rowSeq = 0
const nextRowKey = () => `row-${++rowSeq}`

interface FullJournalEntryFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullJournalEntryForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullJournalEntryFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { closeFormTab } = useTabManagerContext()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modal, modalContextHolder] = Modal.useModal()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  const formTabKey = `journal-entries-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}`

  const accItems = useSelector((state: any) => state.globalConfigs?.acc_items ?? [])
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')

  const [detail, setDetail] = useState<JournalEntryDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [dirty, setDirty] = useState(false)

  const [fundId, setFundId] = useState<string | null>(null)
  const [entryDate, setEntryDate] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [fundTransactionId, setFundTransactionId] = useState<string | null>(null)
  const [assetTransactionId, setAssetTransactionId] = useState<string | null>(null)
  const [lines, setLines] = useState<LineRow[]>([])
  const [options, setOptions] = useState<JournalEntryOptions>({
    lps: [],
    fund_transactions: [],
    asset_transactions: []
  })

  const isBooked = detail?.entry?.status_label === STATUS_LABEL_BOOKED
  const readOnly = isBooked

  const markDirty = useCallback(() => setDirty(true), [])

  const accItemOptions = useMemo(
    () => accItems.map((i: any) => ({ value: i.id, label: i.label })),
    [accItems]
  )

  const hydrateFromDetail = useCallback((d: JournalEntryDetail) => {
    setDetail(d)
    setFundId(d.entry.fund_id)
    setEntryDate(d.entry.entry_date)
    setDescription(d.entry.description ?? '')
    setFundTransactionId(d.entry.fund_transaction_id ?? null)
    setAssetTransactionId(d.entry.asset_transaction_id ?? null)
    setLines(
      d.lines.map((l) => ({
        key: l.id,
        id: l.id,
        acc_item_id: l.item_id ?? null,
        debit_credit_id: (l.debit_credit_id === 'cr' ? 'cr' : 'dr') as 'dr' | 'cr',
        amount: l.amount,
        description: l.description ?? '',
        fund_commitment_id: l.fund_commitment_id ?? null,
        investor_name: l.investor_name ?? null
      }))
    )
    setDirty(false)
  }, [])

  // Cold-load detail (hard-refresh is the normal path).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Journal Entry' }))
      setLines([
        { key: nextRowKey(), acc_item_id: null, debit_credit_id: 'dr', amount: null, description: '', fund_commitment_id: null },
        { key: nextRowKey(), acc_item_id: null, debit_credit_id: 'cr', amount: null, description: '', fund_commitment_id: null }
      ])
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchJournalEntryDetail(String(recordId), token)
      if (cancelled) return
      if (res.error || !res.data) {
        if (res.error) showErrorToast(res.error)
        setNotFound(true)
        setLoading(false)
        return
      }
      hydrateFromDetail(res.data)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `Journal Entry — ${res.data.entry.fund_name ?? ''}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, isNew])

  // Option lists per selected fund (LPs + linkable transactions).
  useEffect(() => {
    if (!fundId) {
      setOptions({ lps: [], fund_transactions: [], asset_transactions: [] })
      return
    }
    let cancelled = false
    const token = getAccessToken(store.getState().auth)
    ;(async () => {
      const res = await fetchJournalEntryOptions(fundId, token)
      if (!cancelled) setOptions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [fundId])

  const totals = useMemo(() => {
    let debits = 0
    let credits = 0
    for (const l of lines) {
      const amt = l.amount ?? 0
      if (l.debit_credit_id === 'dr') debits += amt
      else credits += amt
    }
    return { debits, credits, difference: debits - credits }
  }, [lines])

  // float tolerance (client-side summation; the server's exact numeric
  // check in je__book_entry stays authoritative)
  const isBalanced = Math.abs(totals.difference) < 0.005 && lines.length > 0
  const canPost = !isNew && !dirty && !readOnly && isBalanced && Boolean(detail?.entry?.id)

  const updateLine = useCallback((key: string, updates: Partial<LineRow>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...updates } : l)))
    setDirty(true)
  }, [])

  const addLine = useCallback(() => {
    setLines((prev) => [
      ...prev,
      { key: nextRowKey(), acc_item_id: null, debit_credit_id: 'dr', amount: null, description: '', fund_commitment_id: null }
    ])
    setDirty(true)
  }, [])

  const removeLine = useCallback((key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key))
    setDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!fundId || !entryDate) {
      showErrorToast('Fund and entry date are required')
      return
    }
    const invalid = lines.find((l) => !l.acc_item_id || l.amount == null || l.amount <= 0)
    if (invalid) {
      showErrorToast('Every line needs an accounting item and a positive amount')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: JournalEntrySavePayload = {
        ...(detail?.entry?.id ? { id: detail.entry.id } : { fund_id: fundId }),
        entry_date: entryDate,
        description: description || null,
        fund_transaction_id: fundTransactionId,
        asset_transaction_id: assetTransactionId,
        lines: lines.map((l) => ({
          ...(l.id ? { id: l.id } : {}),
          acc_item_id: l.acc_item_id as string,
          debit_credit_id: l.debit_credit_id,
          amount: l.amount as number,
          description: l.description || null,
          fund_commitment_id: l.fund_commitment_id
        }))
      }
      const res = await saveJournalEntry(payload, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save journal entry')
        return
      }
      const wasNew = !detail?.entry?.id
      hydrateFromDetail(res.data)
      toast.success(wasNew ? 'Journal entry created' : 'Journal entry saved')
      if (wasNew) {
        // Morph the "new" tab into the edit tab in place (no remove+re-add:
        // that left the opener tab visible for one paint — screen flash).
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_JOURNAL_ENTRIES,
            recordId: res.data.entry.id,
            title: `Journal Entry — ${res.data.entry.fund_name ?? ''}`
          })
        )
        navigate(`/${TABLE_JOURNAL_ENTRIES}/forms/full/${res.data.entry.id}`, { replace: true })
      }
    } finally {
      setSaving(false)
    }
  }, [fundId, entryDate, description, fundTransactionId, assetTransactionId, lines, detail, dispatch, formTabKey, hydrateFromDetail, navigate])

  const handleBook = useCallback(async () => {
    if (!detail?.entry?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await bookJournalEntry(detail.entry.id, token)
      if (res.error) {
        const diff = (res.data as any)?.difference
        showErrorToast(diff != null ? `${res.error} (difference: ${diff})` : res.error)
        return
      }
      const refreshed = await fetchJournalEntryDetail(detail.entry.id, token)
      if (refreshed.data) hydrateFromDetail(refreshed.data)
      toast.success('Journal entry posted')
    } finally {
      setActionLoading(false)
    }
  }, [detail, hydrateFromDetail])

  const handleUnbook = useCallback(async () => {
    if (!detail?.entry?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await unbookJournalEntry(detail.entry.id, token)
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      const refreshed = await fetchJournalEntryDetail(detail.entry.id, token)
      if (refreshed.data) hydrateFromDetail(refreshed.data)
      toast.success('Journal entry reverted to Draft')
    } finally {
      setActionLoading(false)
    }
  }, [detail, hydrateFromDetail])

  const handleDelete = useCallback(() => {
    if (!detail?.entry?.id) return
    modal.confirm({
      title: 'Delete this journal entry?',
      content: 'The entry and its journal lines are removed from the ledger.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = await trashJournalEntry(detail.entry.id, token)
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success('Journal entry deleted')
        closeFormTab(formTabKey)
      }
    })
  }, [detail, modal, dispatch, formTabKey, closeFormTab])

  const lineColumns = [
    {
      title: 'Accounting Item',
      dataIndex: 'acc_item_id',
      width: 220,
      render: (_: unknown, row: LineRow) => (
        <Select
          className='w-full'
          value={row.acc_item_id}
          options={accItemOptions}
          placeholder='Select item...'
          showSearch
          optionFilterProp='label'
          disabled={readOnly}
          onChange={(v) => updateLine(row.key, { acc_item_id: v })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (_: unknown, row: LineRow) => (
        <Input
          value={row.description}
          disabled={readOnly}
          onChange={(e) => updateLine(row.key, { description: e.target.value })}
        />
      )
    },
    {
      title: 'Dr / Cr',
      dataIndex: 'debit_credit_id',
      width: 110,
      render: (_: unknown, row: LineRow) => (
        <Select
          className='w-full'
          value={row.debit_credit_id}
          options={[
            { value: 'dr', label: 'Debit' },
            { value: 'cr', label: 'Credit' }
          ]}
          disabled={readOnly}
          onChange={(v) => updateLine(row.key, { debit_credit_id: v as 'dr' | 'cr' })}
        />
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 150,
      render: (_: unknown, row: LineRow) => (
        <InputNumber
          className='w-full'
          value={row.amount}
          {...currencyFmt}
          min={0}
          disabled={readOnly}
          onChange={(v) => updateLine(row.key, { amount: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: 'Investor',
      dataIndex: 'fund_commitment_id',
      width: 200,
      render: (_: unknown, row: LineRow) => (
        <Select
          className='w-full'
          value={row.fund_commitment_id}
          options={readOnly ? bookedLpOptions(row) : options.lps}
          placeholder='(fund level)'
          allowClear
          showSearch
          optionFilterProp='label'
          disabled={readOnly}
          onChange={(v) => updateLine(row.key, { fund_commitment_id: v ?? null })}
        />
      )
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: LineRow) =>
        readOnly ? null : (
          <Button type='text' danger size='small' icon={<DeleteOutlined />} onClick={() => removeLine(row.key)} />
        )
    }
  ]

  if (notFound) {
    return <div className='p-6 text-gray-500'>Journal entry not found.</div>
  }
  if (loading) {
    return <div className='p-6 text-gray-500'>Loading…</div>
  }

  return (
    <div className='h-full overflow-y-auto p-4'>
      {modalContextHolder}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-3'>
          <Typography.Title level={4} className='!mb-0'>
            {isNew && !detail ? 'New Journal Entry' : `Journal Entry — ${detail?.entry?.fund_name ?? ''}`}
          </Typography.Title>
          {detail?.entry?.status_label ? (
            <Tag color={isBooked ? 'green' : 'blue'}>{detail.entry.status_label}</Tag>
          ) : null}
          {isBalanced ? <Tag color='green'>Balanced</Tag> : <Tag color='gold'>Off by {currencyText(totals.difference)}</Tag>}
        </div>
        <div className='flex items-center gap-2'>
          {readOnly ? (
            <Button loading={actionLoading} onClick={() => void handleUnbook()}>
              Unbook
            </Button>
          ) : (
            <>
              {/* Owner spec: primary rightmost; destructive separated leftmost. */}
              {detail?.entry?.id ? (
                <Button danger onClick={handleDelete}>
                  Delete
                </Button>
              ) : null}
              {(isNew && !detail) || dirty ? (
                <Button type='primary' loading={saving} onClick={() => void handleSave()}>
                  {isNew && !detail ? 'Create' : 'Save'}
                </Button>
              ) : null}
              {!((isNew && !detail) || dirty) ? (
                <Button type='primary' disabled={!canPost} loading={actionLoading} onClick={() => void handleBook()}>
                  Post
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        <FormSection title='Details' titleColor='blue' scrollable={false}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <div className='mb-1 text-gray-500 text-sm'>Fund</div>
              <FundSelect
                className='w-full'
                value={fundId}
                placeholder='Select fund...'
                disabled={readOnly || Boolean(detail?.entry?.id)}
                onChange={(v) => {
                  setFundId(v)
                  markDirty()
                }}
              />
            </Col>
            <Col xs={24} md={8}>
              <div className='mb-1 text-gray-500 text-sm'>Entry Date</div>
              <DatePicker
                className='w-full'
                value={entryDate ? dayjs(entryDate) : null}
                format={DATE_FORMAT}
                allowClear={false}
                disabled={readOnly}
                onChange={(d) => {
                  setEntryDate(d ? d.format('YYYY-MM-DD') : null)
                  markDirty()
                }}
              />
            </Col>
            <Col xs={24} md={8} />
            <Col xs={24} md={12}>
              <div className='mb-1 text-gray-500 text-sm'>Linked Fund Transaction (optional)</div>
              <Select
                className='w-full'
                value={fundTransactionId}
                options={options.fund_transactions}
                placeholder='None'
                allowClear
                showSearch
                optionFilterProp='label'
                disabled={readOnly || !fundId}
                onChange={(v) => {
                  setFundTransactionId(v ?? null)
                  markDirty()
                }}
              />
            </Col>
            <Col xs={24} md={12}>
              <div className='mb-1 text-gray-500 text-sm'>Linked Asset Transaction (optional)</div>
              <Select
                className='w-full'
                value={assetTransactionId}
                options={options.asset_transactions}
                placeholder='None'
                allowClear
                showSearch
                optionFilterProp='label'
                disabled={readOnly || !fundId}
                onChange={(v) => {
                  setAssetTransactionId(v ?? null)
                  markDirty()
                }}
              />
            </Col>
            <Col xs={24}>
              <div className='mb-1 text-gray-500 text-sm'>Description</div>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                value={description}
                disabled={readOnly}
                onChange={(e) => {
                  setDescription(e.target.value)
                  markDirty()
                }}
              />
            </Col>
          </Row>
        </FormSection>

        <FormSection
          title='Journal Lines'
          titleColor='green'
          scrollable={false}
          headerActions={
            readOnly ? null : (
              <Button size='small' icon={<PlusOutlined />} onClick={addLine}>
                Add Line
              </Button>
            )
          }
        >
          <Table size='small' columns={lineColumns} dataSource={lines} pagination={false} rowKey='key' />
          <div className='flex justify-end gap-6 mt-2 pr-2'>
            <Text>Debits: {currencyText(totals.debits)}</Text>
            <Text>Credits: {currencyText(totals.credits)}</Text>
            <Text strong>Difference: {currencyText(totals.difference)}</Text>
          </div>
          <Text type='secondary' className='block mt-1'>
            Lines with an Investor stamp the LP subledger dimension (reallocations flow into the
            partners&apos; capital statements). Links to fund / asset transactions live on the entry
            header.
          </Text>
        </FormSection>
      </div>
    </div>
  )
}
